import { useState } from 'react';
import { useAutoAnimate } from '@formkit/auto-animate/react';

// RDS
import Button from '@ramsey-design-system/button';
import Text from '@ramsey-design-system/text';
import Heading from '@ramsey-design-system/heading';
import { ChevronLeftIcon, ChevronRightIcon } from '@ramsey-design-system/icons';
import tokens from '@ramsey-design-system/tokens';

// Types
import { QuizResult } from './hooks/useQuizResults';

export function QuizResults({
  results,
  formatOptionText,
  showQuestion = true
}: {
  results: QuizResult[];
  formatOptionText?: (text: string) => string;
  showQuestion?: boolean;
}) {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const currentQuestion = results[currentQuestionIndex];

  const [animationParent] = useAutoAnimate();

  const handleNext = () => {
    if (currentQuestionIndex < results.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const showNavigation = results.length > 1;

  const maxCount = Math.max(
    ...currentQuestion.options.map((option) => option.count),
    1
  );

  return (
    <div>
      {showQuestion && (
        <Heading
          level="3"
          className="text-4 text-blue-80 -mt-1 mb-4 font-semibold"
        >
          {currentQuestion.question}
        </Heading>
      )}
      <div className="gap-md flex items-center">
        {showNavigation && (
          <Button
            iconOnly
            icon={ChevronLeftIcon}
            onClick={handlePrev}
            appearance="subtle"
            aria-label="Previous Question"
            disabled={currentQuestionIndex === 0}
          />
        )}
        <div className="flex-1">
          <div ref={animationParent} className="mb-2">
            {currentQuestion.options
              .sort((a, b) => b.count - a.count)
              .map((option, index) => {
                const percentWidth =
                  maxCount > 0 ? (option.count / maxCount) * 100 : 0;

                return (
                  <div key={option.text}>
                    <div className="gap-sm flex items-center">
                      <div
                        className="text-5 mb-0.5 flex h-4 items-center justify-start font-semibold text-white duration-500 ease-in-out"
                        style={{
                          width:
                            percentWidth === 0 ? '5px' : `${percentWidth}%`,
                          backgroundColor: BAR_CHART_COLORS[index],
                          paddingLeft: option.count > 0 ? '1.25rem' : '0px'
                        }}
                      >
                        {option.count > 0 && (
                          <span data-testid={`option-${index}-count`}>
                            {option.count}
                          </span>
                        )}
                      </div>
                      {option.count === 0 && (
                        <div
                          className="text-5 text-gray-40 font-semibold"
                          data-testid={`option-${index}-no-responses`}
                        >
                          0
                        </div>
                      )}
                    </div>
                    <Text
                      className="text-1 mb-1.5"
                      data-testid={`option-${index}-text`}
                    >
                      {formatOptionText
                        ? formatOptionText(option.text)
                        : option.text}
                    </Text>
                  </div>
                );
              })}
          </div>
        </div>
        {showNavigation && (
          <Button
            iconOnly
            icon={ChevronRightIcon}
            onClick={handleNext}
            appearance="subtle"
            aria-label="Next Question"
            disabled={currentQuestionIndex === results.length - 1}
          />
        )}
      </div>
    </div>
  );
}

const BAR_CHART_COLORS: Record<number, string> = {
  0: tokens.ColorBaseBlue50,
  1: tokens.ColorBaseAqua50,
  2: tokens.ColorBaseAmber50,
  3: tokens.ColorBaseOrange50
};

export default QuizResults;
